"use client"

import type * as React from "react"
import {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
} from "@/components/ui/pagination"
import { useIsMobile } from "@/hooks/use-mobile"
import { cn } from "@/lib/utils"

interface ModulePaginationProps {
	currentPage: number
	totalPages: number
	onPageChange: (page: number) => void
	maxPagesToShow?: number // Optional: Number of page links to show
}

export function ModulePagination({
	currentPage,
	totalPages,
	onPageChange,
	maxPagesToShow, // Remove default value, will be set based on device
}: ModulePaginationProps) {
	const isMobile = useIsMobile()

	// Set default maxPagesToShow based on device
	const defaultMaxPages = isMobile ? 3 : 5
	const actualMaxPagesToShow = maxPagesToShow ?? defaultMaxPages

	if (totalPages <= 1) {
		return null // Don't render pagination if there's only one page or less
	}

	const handlePreviousPage = (e: React.MouseEvent<HTMLAnchorElement>) => {
		e.preventDefault()
		if (currentPage > 1) {
			onPageChange(currentPage - 1)
		}
	}

	const handleNextPage = (e: React.MouseEvent<HTMLAnchorElement>) => {
		e.preventDefault()
		if (currentPage < totalPages) {
			onPageChange(currentPage + 1)
		}
	}

	const handlePageClick = (
		e: React.MouseEvent<HTMLAnchorElement>,
		page: number,
	) => {
		e.preventDefault()
		onPageChange(page)
	}

	const renderPaginationItems = () => {
		const items: React.ReactNode[] = []
		// Calculate start and end page numbers to display
		let startPage = Math.max(
			1,
			currentPage - Math.floor(actualMaxPagesToShow / 2),
		)
		let endPage = Math.min(totalPages, startPage + actualMaxPagesToShow - 1)

		// Adjust startPage and endPage if the range is smaller than actualMaxPagesToShow
		// and there are enough pages to fill it
		if (endPage - startPage + 1 < actualMaxPagesToShow) {
			if (startPage === 1 && totalPages > actualMaxPagesToShow) {
				endPage = startPage + actualMaxPagesToShow - 1
			} else if (endPage === totalPages && totalPages > actualMaxPagesToShow) {
				startPage = endPage - actualMaxPagesToShow + 1
			}
		}

		// Show ellipsis at the beginning if needed
		if (startPage > 1) {
			items.push(
				<PaginationItem key="start-ellipsis">
					<PaginationEllipsis />
				</PaginationItem>,
			)
		}

		const activeClassName =
			"bg-black hover:bg-black hover:text-white text-white border-none"
		const inactiveClassName =
			"bg-[#f5f5f5] text-black hover:bg-black hover:text-white border-none"
		// Render page numbers
		for (let i = startPage; i <= endPage; i++) {
			items.push(
				<PaginationItem key={i}>
					<PaginationLink
						href="#"
						isActive={i === currentPage}
						onClick={(e) => handlePageClick(e, i)}
						className={cn(
							"md:size-8 font-['DINPro-Medium']",
							isMobile ? "text-base px-2 py-1" : "",
							i === currentPage ? activeClassName : inactiveClassName,
						)}
					>
						{i}
					</PaginationLink>
				</PaginationItem>,
			)
		}

		// Show ellipsis at the end if needed
		if (endPage < totalPages) {
			// Only show ellipsis if there's a gap between endPage and totalPages
			if (endPage < totalPages - 1) {
				items.push(
					<PaginationItem key="end-ellipsis">
						<PaginationEllipsis />
					</PaginationItem>,
				)
			}

			// Always show the last page
			items.push(
				<PaginationItem key={totalPages}>
					<PaginationLink
						href="#"
						isActive={totalPages === currentPage}
						onClick={(e) => handlePageClick(e, totalPages)}
						className={cn(
							"md:size-8 font-['DINPro-Medium']",
							isMobile ? "text-base px-2 py-1" : "",
							totalPages === currentPage ? activeClassName : inactiveClassName,
						)}
					>
						{totalPages}
					</PaginationLink>
				</PaginationItem>,
			)
		}
		return items
	}

	return (
		<Pagination className={isMobile ? "mt-4" : "mt-6"}>
			<PaginationContent className={isMobile ? "gap-1" : ""}>
				<PaginationItem>
					<PaginationPrevious
						href="#"
						onClick={handlePreviousPage}
						className={cn(
							"bg-[#f5f5f5] md:size-8",
							currentPage <= 1 ? "pointer-events-none opacity-50" : "  ",
							"text-base !px-2.5 !py-1",
						)}
					/>
				</PaginationItem>
				{renderPaginationItems()}
				<PaginationItem>
					<PaginationNext
						href="#"
						onClick={handleNextPage}
						className={cn(
							"bg-[#f5f5f5] md:size-8",
							currentPage >= totalPages
								? "pointer-events-none opacity-50"
								: "  ",
							"text-base !px-2.5 !py-1",
						)}
					/>
				</PaginationItem>
			</PaginationContent>
		</Pagination>
	)
}
